import { z } from 'zod'

import type { ZodInfer } from '@mntn-dev/utility-types'

import { refine } from './objects/refine.ts'

/**
 * Creates a Zod schema that when used to parse an object,
 * filters out any keys that do not fit the schema rather than throwing an error.
 *
 * @param schema a Zod schema
 * @returns a schema that filters out any keys that do not fit the given schema
 */
export const FilteredSchema = <T extends z.ZodObject<z.ZodRawShape>>(
  schema: T
) => {
  const hasParse = (value: unknown): value is z.ZodType =>
    typeof (value as z.ZodType).parse === 'function'

  return z.looseObject({}).transform((input) => {
    const parsedObj: Partial<ZodInfer<T>> = {}

    for (const key in input) {
      if (Object.hasOwn(input, key)) {
        const normalizedKey = key.toLowerCase()
        const schemaKey = Object.keys(schema.shape).find(
          (schemaKey) => schemaKey.toLowerCase() === normalizedKey
        )

        if (schemaKey) {
          const fieldSchema = schema.shape[schemaKey]
          if (!fieldSchema || !hasParse(fieldSchema)) {
            continue
          }
          try {
            const value =
              fieldSchema instanceof z.ZodObject
                ? FilteredSchema(fieldSchema).parse(input[key]) // Recursive parsing for objects
                : fieldSchema.parse(input[key])

            parsedObj[schemaKey as keyof ZodInfer<T>] =
              value as ZodInfer<T>[keyof ZodInfer<T>]
          } catch {
            // Skip invalid fields
          }
        }
      }
    }

    return refine(parsedObj) as ZodInfer<T>
  })
}
